import { forwardRef, type ReactNode } from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { type DataTestId } from '../../core/types/data-props.js';
import { type StylingProps } from '../../core/types/styling-props.js';
import { Flex } from '../../layouts/flex/Flex.js';

//#region Typings
/**
 * Accepted properties for the Button Suffix.
 * @public
 */
export interface ButtonSuffixProps
  extends StylingProps, DataTestId, BehaviorTrackingProps {
  /** Elements to be displayed in the Button Suffix slot. */
  children: ReactNode;
}
//#endregion

/**
 * Button Suffix component.
 * @public
 */
export const Suffix = forwardRef<HTMLDivElement, ButtonSuffixProps>(
  (props, forwardedRef) => {
    const {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: ButtonSuffixProps = props;

    const [behavioralTrackingProps] =
      useBehavioralTrackingProps(remainingProps);

    return (
      <Flex
        data-testid={dataTestId}
        gap={0}
        ref={forwardedRef}
        className={consumerClassName}
        style={consumerStyle}
        {...behavioralTrackingProps}
      >
        {children}
      </Flex>
    );
  },
);

(Suffix as typeof Suffix & { displayName: string }).displayName =
  'Button.Suffix';
